import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, MapPin, IndianRupee, ArrowLeft, Sparkles } from "lucide-react";

/**
 * Iter47 — Add another property to an existing chain account.
 *
 * Reuses the same field-set as PartnerOnboarding but posts to
 * /api/my/properties (chain-mode). After a successful add, selects the new
 * property in the switcher so the partner lands on its dashboard.
 */
export default function AddProperty() {
  const navigate = useNavigate();
  const { chain, refresh, select } = useProperty();
  const [form, setForm] = useState({
    name: "", city: "", address: "", contact_name: "", contact_phone: "", contact_email: "",
    base_price: "",
  });
  const [saving, setSaving] = useState(false);
  const [chainLoading, setChainLoading] = useState(true);

  useEffect(() => {
    // Guard: if user isn't in chain mode yet, offer to enable it inline.
    if (chain !== null) setChainLoading(false);
  }, [chain]);

  const enableChain = async () => {
    try {
      await api.patch("/my/chain", { is_chain: true });
      await refresh();
      toast.success("Chain mode enabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not enable chain mode");
    }
  };

  const submit = async () => {
    if (!form.name.trim() || !form.city.trim()) {
      toast.error("Property name + city are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/my/properties", {
        ...form,
        base_price: form.base_price ? Number(form.base_price) : undefined,
      });
      await refresh();
      if (data?.hotel_id) select(data.hotel_id);
      toast.success(`Added ${data.name}. You're now managing this property.`);
      navigate("/partner");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add property failed");
    } finally {
      setSaving(false);
    }
  };

  if (chainLoading) return <div className="max-w-2xl mx-auto py-10 text-center text-slate-500">Loading…</div>;

  const isChain = !!chain?.is_chain;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4" data-testid="add-property-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="hw-card p-8">
        <div className="hw-label text-emerald-700 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Chain expansion</div>
        <h1 className="hw-head text-3xl font-black text-slate-900 mt-1">Add another property</h1>
        <p className="text-sm text-slate-600 mt-2">
          {chain?.company_name && (<><b>{chain.company_name}</b> currently manages <b>{chain.property_count}</b> propert{chain.property_count === 1 ? "y" : "ies"}. Add another to expand your SwaanStay footprint.</>)}
          {!chain?.company_name && <>Add another property under your chain account.</>}
        </p>

        {!isChain && (
          <div className="mt-5 border border-amber-300 bg-amber-50 rounded-md p-4">
            <div className="font-bold text-amber-900">Chain mode is not enabled yet.</div>
            <p className="text-sm text-amber-900 mt-1">Turn it on to add more than one property. You can always disable it later.</p>
            <Button onClick={enableChain} className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-sm font-bold" data-testid="enable-chain">
              Enable Chain Mode
            </Button>
          </div>
        )}

        {isChain && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="add-property-form">
            <div className="md:col-span-2">
              <Label className="hw-label">Property name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. SwaanStay Goa Beachfront"
                className="rounded-sm mt-1"
                data-testid="add-name"
              />
            </div>
            <div>
              <Label className="hw-label">City *</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Goa" className="rounded-sm mt-1" data-testid="add-city" />
            </div>
            <div>
              <Label className="hw-label">Base price (₹ / night)</Label>
              <Input type="number" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} placeholder="3500" className="rounded-sm mt-1" data-testid="add-base-price" />
            </div>
            <div className="md:col-span-2">
              <Label className="hw-label">Full address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Beach Rd, Baga" className="rounded-sm mt-1" data-testid="add-address" />
            </div>
            <div>
              <Label className="hw-label">Contact name</Label>
              <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Property manager" className="rounded-sm mt-1" data-testid="add-contact-name" />
            </div>
            <div>
              <Label className="hw-label">Contact phone</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+91 98xxxxxxxx" className="rounded-sm mt-1" data-testid="add-contact-phone" />
            </div>
            <div className="md:col-span-2">
              <Label className="hw-label">Contact email</Label>
              <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="manager@example.com" className="rounded-sm mt-1" data-testid="add-contact-email" />
            </div>
            <div className="md:col-span-2 flex gap-2 mt-2">
              <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="add-submit">
                {saving ? "Adding..." : "Add property"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/partner")} className="rounded-sm" data-testid="add-cancel">Cancel</Button>
            </div>
            <p className="md:col-span-2 text-xs text-slate-500 italic">
              After adding: switch to the new property via the switcher in the header → complete rooms, photos, and connect its PMS.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
